import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Clock, Check, Loader2, Building2, MapPin, CheckCircle2, XCircle, Inbox, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CardHead = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="cn-eyebrow">{eyebrow}</p>
      <h3 className="cn-display text-lg text-cn-text dark:text-white leading-tight">{title}</h3>
    </div>
  </div>
);

const WORK_TYPES = [
  { id: 'temporary', name: 'Temporary / Locum / Per-diem', icon: Clock },
  { id: 'permanent', name: 'Permanent / Full-time', icon: Briefcase },
];

/* ---------------- Clinician: opt in to agency work ---------------- */
export const ClinicianAgencyCard = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [availability, setAvailability] = useState('');

  useEffect(() => {
    axios.get(`${API}/clinician/profile`, getAuthHeader())
      .then((r) => {
        const p = r.data || {};
        setOpen(!!p.open_to_agency_work);
        setTypes(p.agency_work_types || []);
        setAvailability(p.agency_availability || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleType = (id) => setTypes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/clinician/agency-availability`, {
        open_to_agency_work: open,
        agency_work_types: types,
        agency_availability: availability || null,
      }, getAuthHeader());
      setTypes(data.agency_work_types || []);
      toast.success('Agency availability saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-cn-primary animate-spin" /></div>;

  return (
    <div data-testid="clinician-agency-card">
      <CardHead icon={Building2} eyebrow="Work" title="Agency & organization work" />
      <label className="mt-5 flex items-center justify-between gap-4 p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60 cursor-pointer">
        <div>
          <p className="font-semibold text-cn-text dark:text-white text-sm">Open to agency / org work</p>
          <p className="text-xs text-cn-text-muted mt-0.5">Let staffing agencies & health systems send you temp or permanent offers.</p>
        </div>
        <Switch checked={open} onCheckedChange={setOpen} data-testid="clinician-open-agency-switch" />
      </label>

      {open && (
        <div className="mt-5 space-y-5 animate-fade-in-up">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Open to</p>
            <div className="flex flex-wrap gap-2">
              {WORK_TYPES.map((w) => {
                const on = types.includes(w.id);
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => toggleType(w.id)}
                    aria-pressed={on}
                    data-testid={`clinician-worktype-${w.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      on ? 'bg-cn-primary text-white border-cn-primary shadow-sm' : 'bg-white dark:bg-white/5 text-cn-text border-cn-border hover:border-cn-primary/40'
                    }`}
                  >
                    <w.icon className="w-3.5 h-3.5" /> {w.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Availability note (optional)</p>
            <Textarea value={availability} onChange={(e) => setAvailability(e.target.value)} placeholder="e.g. Per-diem weekends; open to permanent roles in Q3." className="min-h-[64px] rounded-2xl border-cn-border bg-white dark:bg-white/5" data-testid="clinician-agency-availability" />
          </div>
        </div>
      )}

      <Button onClick={save} disabled={saving} className="cn-btn cn-btn-primary w-full mt-5 !py-2.5" data-testid="clinician-save-agency">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save availability'}
      </Button>
    </div>
  );
};

/* ---------------- Clinician: engagement requests inbox ---------------- */
export const ClinicianEngagementsInbox = ({ getAuthHeader }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios.get(`${API}/engagements`, getAuthHeader())
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const respond = async (id, status) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/engagements/${id}/respond`, { status }, getAuthHeader());
      toast.success(`Engagement ${status}`);
      setItems((prev) => prev.map((e) => e.id === id ? { ...e, status } : e));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally { setBusyId(null); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: 'bg-cn-warning/10 text-cn-warning border-cn-warning/25',
      accepted: 'bg-cn-success/10 text-cn-success border-cn-success/25',
      declined: 'bg-cn-border/40 text-cn-text-muted border-cn-border',
    };
    return <Badge className={`border ${map[s] || map.pending} capitalize`}>{s}</Badge>;
  };

  return (
    <div data-testid="clinician-engagements-inbox">
      <CardHead icon={Inbox} eyebrow="Opportunities" title="Agency engagement requests" />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-cn-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="mt-5 text-sm text-cn-text-muted">No engagement requests yet. Turn on "Open to agency work" above so agencies can reach you.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {items.map((e) => (
            <div key={e.id} className="rounded-2xl border border-cn-border/70 p-4 bg-cn-bg/40" data-testid={`engagement-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary bg-cn-sage-100 px-2 py-0.5 rounded-full capitalize">
                      {e.engagement_type === 'permanent' ? <Briefcase className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{e.engagement_type}
                    </span>
                    {statusBadge(e.status)}
                  </div>
                  <h4 className="font-display font-semibold text-cn-text mt-2 leading-snug">{e.title}</h4>
                  <p className="text-xs text-cn-text-muted mt-0.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> {e.agency_name}{e.location ? <> · <MapPin className="w-3 h-3" /> {e.location}</> : null}</p>
                  {(e.duration || e.rate) && <p className="text-xs text-cn-text-muted mt-1">{[e.duration, e.rate].filter(Boolean).join(' · ')}</p>}
                  {e.description && <p className="text-sm text-cn-text mt-2 leading-relaxed">{e.description}</p>}
                </div>
              </div>
              {e.status === 'pending' && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => respond(e.id, 'accepted')} disabled={busyId === e.id} className="cn-btn cn-btn-primary text-sm flex-1 !py-2" data-testid={`engagement-accept-${e.id}`}>
                    {busyId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Accept
                  </button>
                  <button onClick={() => respond(e.id, 'declined')} disabled={busyId === e.id} className="cn-btn cn-btn-ghost text-sm flex-1 !py-2" data-testid={`engagement-decline-${e.id}`}>
                    <XCircle className="w-4 h-4" /> Decline
                  </button>
                </div>
              )}
              {e.status === 'accepted' && (
                <button
                  onClick={() => navigate(`/clinician/messages?with=${e.agency_id}&name=${encodeURIComponent(e.agency_name || '')}`)}
                  className="cn-btn cn-btn-primary text-sm mt-3 !py-2"
                  data-testid={`engagement-message-${e.id}`}
                >
                  <MessageSquare className="w-4 h-4" /> Message {e.agency_name?.split(' ')[0] || 'agency'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
